"use client";

import { ChevronLeft } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import ProductBottomSheet from "./ProductBottomSheet";

type Props = {
  title: string;
  sheetTitle?: string;
  viewAllLabel: string;
  items: (truncate: boolean) => ReactNode[];
  sheetContent: ReactNode;
  framed?: boolean;
  /** Title click opens the sheet (strip still scrolls). */
  disclosure?: boolean;
};

export default function ProductMobileScrollStrip({
  title,
  sheetTitle,
  viewAllLabel,
  items,
  sheetContent,
  framed,
  disclosure,
}: Props) {
  const [open, setOpen] = useState(false);
  const [truncate, setTruncate] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const fullItems = items(false);
  const visibleItems = items(truncate);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;

    const check = () => setTruncate(measure.scrollWidth > row.clientWidth + 1);
    check();

    const ro = new ResizeObserver(check);
    ro.observe(row);
    ro.observe(measure);
    return () => ro.disconnect();
  }, [fullItems.length]);

  if (!fullItems.length) return null;

  const openSheet = () => setOpen(true);

  const row = (
    <Box ref={rowRef} sx={{ display: "flex", alignItems: "stretch", position: "relative", mx: framed ? 0 : { xs: -0.5, sm: 0 } }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          gap: 0.75,
          px: framed ? 0 : 0.5,
        }}
      >
        <Box ref={measureRef} sx={{ display: "flex", gap: 0.75 }}>
          {fullItems.map((item, index) => (
            <Box key={index} sx={{ flexShrink: 0 }}>
              {item}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          flex: 1,
          minWidth: 0,
          py: framed ? 0 : 0.25,
          px: framed ? 0 : 0.5,
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {visibleItems.map((item, index) => (
            <Box
              key={index}
              onClick={disclosure ? openSheet : undefined}
              sx={{
                scrollSnapAlign: "start",
                flexShrink: 0,
                cursor: disclosure ? "pointer" : undefined,
              }}
            >
              {item}
            </Box>
          ))}
        </Box>
      </Box>

      {truncate && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "stretch",
            position: "relative",
            pl: 0.25,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              right: "100%",
              width: 20,
              pointerEvents: "none",
              background: (theme) =>
                `linear-gradient(to left, ${framed ? theme.palette.background.paper : theme.palette.background.default}, transparent)`,
            },
          }}
        >
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={openSheet}
            endIcon={<ChevronLeft sx={{ fontSize: 16 }} />}
            sx={{
              px: 0.75,
              minWidth: "auto",
              height: "100%",
              borderRadius: 1.5,
              fontSize: "0.7rem",
              lineHeight: 1,
              whiteSpace: "nowrap",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "grey.100",
              "& .MuiButton-endIcon": { ml: 0, mr: -0.25 },
            }}
          >
            {viewAllLabel}
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box data-mobile-scroll-strip sx={{ display: { xs: "block", md: "none" } }}>
      <Typography
        component={disclosure ? "button" : "p"}
        variant="subtitle2"
        color="grey.800"
        onClick={disclosure ? openSheet : undefined}
        sx={{
          mb: 0.75,
          fontWeight: 600,
          ...(disclosure
            ? {
                display: "block",
                width: "100%",
                m: 0,
                mb: 0.75,
                p: 0,
                border: 0,
                bgcolor: "transparent",
                font: "inherit",
                fontWeight: 600,
                textAlign: "start",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
              }
            : null),
        }}
      >
        {title}
      </Typography>

      {framed ? (
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "grey.100",
            px: { xs: 1.5, sm: 2.5 },
            py: 1.5,
            minWidth: 0,
            overflowX: "clip",
          }}
        >
          {row}
        </Box>
      ) : (
        row
      )}

      <ProductBottomSheet open={open} onClose={() => setOpen(false)} title={sheetTitle ?? title}>
        {sheetContent}
      </ProductBottomSheet>
    </Box>
  );
}
